import { useKpis } from '@/hooks/useAdminStats'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { DollarSign, TrendingUp, Users, CreditCard } from 'lucide-react'
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts'

const TIER_COLORS: Record<string, string> = {
  trial: '#94a3b8',     // slate
  expired: '#6b7280',   // gray
  pilot: '#3b82f6',     // blue
  pro: '#8b5cf6',       // violet
  business: '#64A735',  // brand green — the tier above Pro
}

// Mirrors PLAN_PRICES in apps/web/src/lib/utils/featureGating.ts — the admin
// app is a separate build and cannot import it. Kept honest by
// apps/web/src/lib/__tests__/pricingParity.test.ts.
const PLAN_PRICES = {
  pilot: { monthly: 8, annual: 48, lifetime: 88 },
  pro: { monthly: 16, annual: 144 },
  business: { monthly: 48, annual: 480 },
}

const PLAN_LABELS: Record<string, string> = {
  pilot: 'Pilot', pro: 'Pro', business: 'Business',
  monthly: 'Monthly', quarterly: 'Quarterly', annual: 'Annual',
}

// One bar per `${tier}_${cycle}` from admin-stats mrrByPlan, in this order;
// monthly is the lighter shade of its tier colour.
const BREAKDOWN_COLORS: Record<string, string> = {
  pilot_monthly: '#60a5fa',
  pilot_annual: '#3b82f6',
  pro_monthly: '#a78bfa',
  pro_annual: '#8b5cf6',
  business_monthly: '#8fc764',
  business_annual: '#64A735',
}

interface KpiCardProps {
  title: string
  value: string | number
  subtitle?: string
  icon: React.ElementType
  loading?: boolean
}

function KpiCard({ title, value, subtitle, icon: Icon, loading }: KpiCardProps) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            {loading ? (
              <Skeleton className="mt-1 h-8 w-24" />
            ) : (
              <p className="mt-1 text-2xl font-bold">{value}</p>
            )}
            {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export function RevenueDashboardPage() {
  const { data: kpis, isLoading } = useKpis()

  const tierData = kpis ? [
    { name: 'Trial', value: kpis.tierCounts.trial, color: TIER_COLORS.trial },
    { name: 'Expired', value: kpis.tierCounts.expired, color: TIER_COLORS.expired },
    { name: 'Pilot', value: kpis.tierCounts.pilot, color: TIER_COLORS.pilot },
    { name: 'Pro', value: kpis.tierCounts.pro, color: TIER_COLORS.pro },
    { name: 'Business', value: kpis.tierCounts.business, color: TIER_COLORS.business },
  ].filter(d => d.value > 0) : []

  // Real per-cycle split from admin-stats. An older deployment without
  // mrrByPlan renders the empty state rather than an invented split.
  const breakdownOrder = Object.keys(BREAKDOWN_COLORS)
  const revenueBreakdown = Object.entries(kpis?.mrrByPlan ?? {})
    .map(([key, plan]) => {
      const [tier, cycle] = key.split('_')
      return {
        key,
        tier: `${PLAN_LABELS[tier] ?? tier} ${PLAN_LABELS[cycle] ?? cycle}`,
        mrr: plan.mrr,
        color: BREAKDOWN_COLORS[key] ?? TIER_COLORS[tier] ?? TIER_COLORS.trial,
      }
    })
    .filter(d => d.mrr > 0)
    .sort((a, b) => {
      const ai = breakdownOrder.indexOf(a.key)
      const bi = breakdownOrder.indexOf(b.key)
      return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi)
    })

  const arr = (kpis?.mrr ?? 0) * 12
  const conversionRate = kpis && kpis.totalUsers > 0
    ? ((kpis.activeSubscribers / kpis.totalUsers) * 100).toFixed(1)
    : '0'

  return (
    <div>
      <PageHeader title="Revenue" description="Subscription metrics and billing analytics" />

      {/* KPI Cards */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="MRR"
          value={`$${kpis?.mrr ?? 0}`}
          subtitle="Monthly Recurring Revenue"
          icon={DollarSign}
          loading={isLoading}
        />
        <KpiCard
          title="ARR"
          value={`$${arr}`}
          subtitle="Annual Recurring Revenue"
          icon={TrendingUp}
          loading={isLoading}
        />
        <KpiCard
          title="Paid Subscribers"
          value={kpis?.activeSubscribers ?? 0}
          subtitle={`${kpis?.tierCounts.pilot ?? 0} Pilot, ${kpis?.tierCounts.pro ?? 0} Pro, ${kpis?.tierCounts.business ?? 0} Business`}
          icon={Users}
          loading={isLoading}
        />
        <KpiCard
          title="Conversion Rate"
          value={`${conversionRate}%`}
          subtitle="Paid / Total Users"
          icon={CreditCard}
          loading={isLoading}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Tier Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">User Distribution by Tier</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : (
              <div className="flex items-center gap-8">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie
                      data={tierData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="value"
                      strokeWidth={2}
                    >
                      {tierData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-3">
                  {tierData.map((d) => (
                    <div key={d.name} className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-sm">{d.name}</span>
                      <span className="ml-auto text-sm font-semibold">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">MRR Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : revenueBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={revenueBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="tier" className="text-xs" />
                  <YAxis className="text-xs" tickFormatter={(v: number) => `$${v}`} />
                  <RechartsTooltip formatter={(v: number) => [`$${v.toFixed(2)}`, 'MRR']} />
                  <Bar dataKey="mrr" radius={[4, 4, 0, 0]}>
                    {revenueBreakdown.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground">No paid subscribers yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Pricing Reference */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Pricing Reference</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4">Tier</th>
                    <th className="pb-2 pr-4">Monthly</th>
                    <th className="pb-2 pr-4">Annual</th>
                    <th className="pb-2 pr-4">Lifetime</th>
                    <th className="pb-2">Effective Monthly</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 pr-4 font-medium">Pilot</td>
                    <td className="py-2 pr-4">${PLAN_PRICES.pilot.monthly}/mo</td>
                    <td className="py-2 pr-4">${PLAN_PRICES.pilot.annual}/yr</td>
                    <td className="py-2 pr-4">${PLAN_PRICES.pilot.lifetime}</td>
                    <td className="py-2">${(PLAN_PRICES.pilot.annual / 12).toFixed(0)}/mo (annual)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4 font-medium">Pro</td>
                    <td className="py-2 pr-4">${PLAN_PRICES.pro.monthly}/mo</td>
                    <td className="py-2 pr-4">${PLAN_PRICES.pro.annual}/yr</td>
                    <td className="py-2 pr-4">—</td>
                    <td className="py-2">${(PLAN_PRICES.pro.annual / 12).toFixed(0)}/mo (annual)</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium">Business</td>
                    <td className="py-2 pr-4">${PLAN_PRICES.business.monthly}/mo</td>
                    <td className="py-2 pr-4">${PLAN_PRICES.business.annual}/yr</td>
                    <td className="py-2 pr-4">—</td>
                    <td className="py-2">${(PLAN_PRICES.business.annual / 12).toFixed(0)}/mo (annual)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
